import Vue from 'vue';
import './plugins/axios';
import App from './App.vue';
import vuetify from './plugins/vuetify';
import router from './router';
import store from './store';
import VueGtm from '@gtm-support/vue2-gtm';
import VuePhoneNumberInput from 'vue-phone-number-input';
import 'vue-phone-number-input/dist/vue-phone-number-input.css';
import moment from 'vue-moment';

import VCurrencyField from 'v-currency-field';

import translationsLt from './i18n/lt.json';
require('./plugins/replaceAllPolyfill.js');

import * as Sentry from '@sentry/vue';
import { kioskSentryMatcher, KIOSK_PAYMENT_TAG } from './utils/kioskSentry';

import LottieVuePlayer from '@lottiefiles/vue-lottie-player';

import { initializeApp } from 'firebase/app';

Vue.config.productionTip = false;

const firebaseConfig = {
  apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
  authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.VUE_APP_FIREBASE_APP_ID,
  measurementId: process.env.VUE_APP_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase
initializeApp(firebaseConfig);

if (process.env.VUE_APP_ENVIRONMENT === 'production') {
  Vue.use(VueGtm, {
    id: 'GTM-MBPBCQ5',
    enabled: false,
  });
}

const TIPS_COLOR_VARIANTS = {
  default: '#E85941',
  pinkColdSoupFestival: '#ed2891',
};

Vue.mixin({
  data: function() {
    return {
      maxAppWidth: 800,
      tipsColorGlobal: TIPS_COLOR_VARIANTS.default,
      donateColorGlobal: '#E85941',
      greetInstagram: 'https://www.instagram.com/greet.app',
      greetFacebook: 'https://www.facebook.com/greet.menu',
      greetWebsite: 'https://greet.lt',
    };
  },
});

Vue.component('vue-phone-number-input', VuePhoneNumberInput);
Vue.use(LottieVuePlayer);
Vue.use(VCurrencyField, {
  decimalLength: 2,
  autoDecimalMode: false,
  defaultValue: 0,
  valueAsInteger: false,
  allowNegative: false,
});

Vue.use(moment);

// add translations directly to the application
Vue.i18n.add('lt', translationsLt);
// set the start locale to use
Vue.i18n.set('lt');

if (process.env.VUE_APP_ENVIRONMENT === 'production') {
  Sentry.init({
    Vue,
    dsn:
      'https://0e4771fb0c0343f38cd99a56f3366523@o4504293931024384.ingest.sentry.io/4504293933711360',
    // kiosk payment events are routed to their own project (see kioskSentry.js)
    transport: Sentry.makeMultiplexedTransport(
      Sentry.makeFetchTransport,
      kioskSentryMatcher
    ),
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration(),
    ],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.3,
    tracePropagationTargets: ['app.greet.menu', /^\//],
    replaysOnErrorSampleRate: 0.1,
    // a kiosk sale can run for minutes; keep enough history to see the
    // whole terminal exchange on a stuck payment
    maxBreadcrumbs: 200,
    beforeBreadcrumb(breadcrumb) {
      // the kiosk polls the terminal every 2s; these lines would push the
      // useful terminal breadcrumbs out of the buffer
      const message = breadcrumb.message || '';
      if (
        breadcrumb.category === 'console' &&
        (message.startsWith('WORLDLINE: Already connected') ||
          message.startsWith('WORLDLINE: Socket message'))
      ) {
        return null;
      }
      return breadcrumb;
    },
  });

  // uncaught errors and failed requests on the kiosk payment screens belong
  // to the kiosk payments project as well
  const KIOSK_PAYMENT_ROUTES = ['PayInPerson', 'PayInPersonSuccess'];
  router.afterEach(to => {
    const onKioskPayment =
      store.getters.isKiosk && KIOSK_PAYMENT_ROUTES.includes(to.name);
    Sentry.setTag(
      KIOSK_PAYMENT_TAG.key,
      onKioskPayment ? KIOSK_PAYMENT_TAG.value : undefined
    );
  });
}

const app = new Vue({
  vuetify,
  router,
  store,
  render: h => h(App),
});

app.$mount('#app');

if (process.env.NODE_ENV !== 'production') {
  window.__app__ = app;
  window.__store__ = store;
}
